import {
  AccountType,
  Driver_Read,
  getV1CompaniesAccountsTypeahead,
  GetV1CompaniesAccountsTypeaheadData,
  getV1CompaniesCompanyIdDriversTypeahead,
  GetV1CompaniesCompanyIdDriversTypeaheadData,
  getV1CompaniesCompanyIdJobs,
  getV1CompaniesCompanyIdJobsBulkSendDayDrivers,
  getV1CompaniesCompanyIdJobsBulkSendDayVendors,
  GetV1CompaniesCompanyIdJobsData,
  Job_Read,
  JobBulkSendDayAssignees_Request,
  JobState,
  patchV1CompaniesCompanyIdJobsId,
  putV1CompaniesCompanyIdJobsBulkSendDayDrivers,
  putV1CompaniesCompanyIdJobsBulkSendDayVendors,
  putV1CompaniesCompanyIdJobsJobIdAssignVendor,
  putV1CompaniesCompanyIdJobsJobIdUnassign,
} from '@treadinc/horizon-api-spec';
import axios from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { chunk, omit } from 'lodash-es';
import { useCallback, useContext } from 'react';

import { JobAssignmentType } from '~constants/enums';
import BulkSendProgressContext from '~contexts/BulkSendProgressContext';
import { AccountTypeahead } from '~hooks/useAccount/models';
import useCancelToken from '~hooks/useCancelToken/useCancelToken';
import { DriverBasic } from '~hooks/useDrivers/models';
import { Job } from '~hooks/useJob/models';
import useLatestRequestSignal from '~hooks/useLatestRequestSignal/useLatestRequestSignal';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { AssigneesFilters, JobsFilters } from '~store/DriverSchedulerStore';
import { Nullable } from '~types/Nullable';
import { fetchAllApiPages } from '~utils/fetchAllPages';

import {
  DUMMY_COLUMN_INCREMENTS,
  INCREMENTS_PER_HOUR,
  MINUTES_PER_INCREMENT,
} from '../components/drivers/constants';

interface FetchAssigneesParams {
  companyId: string;
  loadMore?: boolean;
}

type GetDriversQueryProps = NonNullable<
  GetV1CompaniesCompanyIdDriversTypeaheadData['query']
>;

type GetVendorsQueryProps = NonNullable<GetV1CompaniesAccountsTypeaheadData['query']>;

interface FetchJobsParams {
  assigneeIds?: string[];
  states?: JobState[];
  companyId: string;
}

type GetJobsQueryProps = NonNullable<GetV1CompaniesCompanyIdJobsData['query']>;

interface BulkSendDayJobsParams {
  assignedJobsFilters: JobsFilters;
  assigneesFilters: AssigneesFilters;
  companyId: string;
  date: string;
}

function buildJobsFacetQuery(jobsFilters: JobsFilters) {
  const params: Pick<
    GetJobsQueryProps,
    | 'filter[customer_account_ids]'
    | 'filter[dropoff_site_ids]'
    | 'filter[external_ids]'
    | 'filter[order][dispatch_number_ids]'
    | 'filter[pickup_site_ids]'
    | 'filter[project_ids]'
    | 'search[datagrid]'
  > = {};

  if (jobsFilters.search?.trim().length) {
    params['search[datagrid]'] = jobsFilters.search.trim();
  }

  if (jobsFilters.customers?.length) {
    params['filter[customer_account_ids]'] = jobsFilters.customers;
  }

  if (jobsFilters.dispatchNumberIds?.length) {
    params['filter[order][dispatch_number_ids]'] = jobsFilters.dispatchNumberIds;
  }

  if (jobsFilters.dropOffSites?.length) {
    params['filter[dropoff_site_ids]'] = jobsFilters.dropOffSites;
  }

  if (jobsFilters.pickUpSites?.length) {
    params['filter[pickup_site_ids]'] = jobsFilters.pickUpSites;
  }

  if (jobsFilters.projects?.length) {
    params['filter[project_ids]'] = jobsFilters.projects;
  }

  if (jobsFilters.projectsExternalIds?.length) {
    params['filter[external_ids]'] = jobsFilters.projectsExternalIds;
  }

  return params;
}

function buildBulkSendDayJobsRequest({
  assignedJobsFilters,
  assigneesFilters,
  date,
}: Omit<BulkSendDayJobsParams, 'companyId'>) {
  const idsOrOmitted = (ids?: string[]) => (ids?.length ? ids : undefined);
  const dispatchNumberIds = idsOrOmitted(assignedJobsFilters.dispatchNumberIds);
  const datagridSearch = assignedJobsFilters.search?.trim();
  const assigneeSearch = assigneesFilters.search;

  const request: JobBulkSendDayAssignees_Request = {
    date,
    filter: {
      customer_account_ids: idsOrOmitted(assignedJobsFilters.customers),
      dropoff_site_ids: idsOrOmitted(assignedJobsFilters.dropOffSites),
      external_ids: idsOrOmitted(assignedJobsFilters.projectsExternalIds),
      pickup_site_ids: idsOrOmitted(assignedJobsFilters.pickUpSites),
      project_ids: idsOrOmitted(assignedJobsFilters.projects),
      ...(dispatchNumberIds ? { order: { dispatch_number_ids: dispatchNumberIds } } : {}),
    },
    search: {
      ...(datagridSearch ? { datagrid: datagridSearch } : {}),
      ...(assigneeSearch ? { query: assigneeSearch } : {}),
    },
  };

  return request;
}

type UpdateJobFormData = Parameters<typeof Job.deparseUpdate>[0];
type UpdateJobInlineData = Parameters<typeof Job.deparseUpdateFromInlineMode>[0];
type UpdateJobData = UpdateJobFormData | UpdateJobInlineData;

export default function useDriverSchedulerFetch() {
  const { driverSchedulerStore } = useStores();
  const { enqueueBulkSend } = useContext(BulkSendProgressContext);
  const fetchUnassignedJobsCancelToken = useCancelToken();
  const fetchUnassignedJobsPage = async (options: FetchJobsParams) => {
    driverSchedulerStore.unassignedJobsFetchStart();

    // Anything that re-scopes the unassigned collection bumps this generation.
    // Each component calling useDriverSchedulerFetch holds its own cancel token,
    // so sibling instances never cancel one another and a request has to check
    // for itself that the list it is about to write to is still the one it
    // started against. Every exit consults this: settling a re-scoped request
    // through fetchUnassignedJobsEnd would rewrite unassignedJobsPagination from
    // its absent pagination argument and erase the newer load's cursor.
    const generationAtRequestStart = driverSchedulerStore.unassignedLoadGeneration;
    const listWasRescoped = () =>
      driverSchedulerStore.unassignedLoadGeneration !== generationAtRequestStart;

    const { unassignedJobsPagination, unassignedJobsFilters, dateFilters } =
      driverSchedulerStore;

    const params: GetJobsQueryProps = {
      'page[limit]': unassignedJobsPagination.limit,
    };

    if (unassignedJobsPagination.before) {
      params['page[before]'] = unassignedJobsPagination.before;
    } else if (unassignedJobsPagination.after) {
      params['page[after]'] = unassignedJobsPagination.after;
    }

    if (dateFilters.startDate) {
      params['filter[start_date]'] = dayjs
        .tz(dateFilters.startDate)
        .startOf('day')
        .toISOString();
    }

    if (dateFilters.endDate) {
      params['filter[end_date]'] = dayjs
        .tz(dateFilters.endDate)
        .endOf('day')
        .toISOString();
    }

    if (options.states) {
      params['filter[states]'] = options.states;
    }

    Object.assign(params, buildJobsFacetQuery(unassignedJobsFilters));

    const cancelToken = fetchUnassignedJobsCancelToken.issueToken();

    try {
      const response = await getV1CompaniesCompanyIdJobs({
        path: { 'company-id': options.companyId },
        query: params,
        cancelToken,
      });
      const parsedJobs = response.data.data.map((job) => Job.parse(job));
      const pagination = extractPagination(response);

      if (listWasRescoped()) {
        driverSchedulerStore.unassignedJobsFetchAbandoned();
        return { jobs: parsedJobs, wasCancelled: false };
      }

      driverSchedulerStore.fetchUnassignedJobsEnd(
        parsedJobs,
        pagination,
        options.companyId,
      );
      return { jobs: parsedJobs, wasCancelled: false };
    } catch (error) {
      if (axios.isCancel(error)) {
        driverSchedulerStore.unassignedJobsFetchAbandoned();
        return { jobs: [], wasCancelled: true };
      }

      if (listWasRescoped()) {
        driverSchedulerStore.unassignedJobsFetchAbandoned();
      } else {
        driverSchedulerStore.fetchUnassignedJobsEnd([]);
      }

      connection.handleRequestError(error, t('error_messages.jobs.failed_to_fetch'));
      throw error;
    } finally {
      fetchUnassignedJobsCancelToken.discardToken();
    }
  };

  const fetchUnassignedJobs = async (options: FetchJobsParams) => {
    const { jobs } = await fetchUnassignedJobsPage(options);

    return jobs;
  };

  const fetchAllUnassignedJobs = async (options: FetchJobsParams) => {
    // Start from a clean slate and claim this generation. If a later load starts
    // (e.g. the filters change mid-load), the generation no longer matches and we
    // stop writing so the newer load wins — no mixed-criteria results.
    const generation = driverSchedulerStore.startEagerUnassignedLoad();
    const isCurrentLoad = () =>
      generation === driverSchedulerStore.unassignedLoadGeneration;

    try {
      let hasNext = true;
      while (hasNext && isCurrentLoad()) {
        // A cancelled page leaves pagination untouched, which is indistinguishable
        // from an exhausted cursor. Bail without marking the collection complete so
        // a later eager load retries instead of skipping every page.
        const { wasCancelled } = await fetchUnassignedJobsPage(options);

        if (wasCancelled) {
          return;
        }

        hasNext = !!driverSchedulerStore.unassignedJobsPagination.after;
      }

      if (isCurrentLoad()) {
        driverSchedulerStore.setUnassignedJobsFullyLoaded(true);
      }
    } finally {
      // Only release the loading flag if a newer load has not taken over.
      if (isCurrentLoad()) {
        driverSchedulerStore.setEagerLoadingUnassignedJobs(false);
      }
    }
  };

  const issueAssignedJobsRequestSignal = useLatestRequestSignal();
  const fetchAssignedJobs = async (options: FetchJobsParams) => {
    if (!options.assigneeIds || options.assigneeIds.length === 0) {
      return;
    }

    driverSchedulerStore.assignedJobsFetchStart();

    const { assignedJobsFilters, dateFilters } = driverSchedulerStore;

    const isTargetingVendor =
      driverSchedulerStore.assigneesFilters.assigneeType === 'vendor';
    const params: GetJobsQueryProps = {};

    if (dateFilters.startDate) {
      params['filter[start_date]'] = dayjs
        .tz(dateFilters.startDate)
        .startOf('day')
        .toISOString();
    }

    if (dateFilters.endDate) {
      params['filter[end_date]'] = dayjs
        .tz(dateFilters.endDate)
        .endOf('day')
        .toISOString();
    }

    Object.assign(params, buildJobsFacetQuery(assignedJobsFilters));

    // Chunk assignee IDs into groups of 20
    const assigneeIdChunks = chunk(options.assigneeIds || [], 20);
    const newAssignments: Record<string, Nullable<Job>> = {};
    const assigneeJobCount: Record<string, number> = {};

    const requestSignal = issueAssignedJobsRequestSignal();
    const chunkRequestsController = new AbortController();
    requestSignal.addEventListener('abort', () => chunkRequestsController.abort());

    const fetchAllJobsOfAssignees = (assigneeIdsChunk: string[]) => {
      const chunkParams: GetJobsQueryProps = { ...params };

      if (isTargetingVendor) {
        chunkParams['filter[vendor_account_ids]'] = assigneeIdsChunk;
      } else {
        chunkParams['filter[driver_ids]'] = assigneeIdsChunk;
      }

      return fetchAllApiPages(
        (query, pageSignal) =>
          getV1CompaniesCompanyIdJobs({
            path: { 'company-id': options.companyId },
            query: { ...chunkParams, ...query },
            signal: pageSignal,
          }),
        Job.parse,
        { signal: chunkRequestsController.signal },
      );
    };

    try {
      const allFetchedJobs = (
        await Promise.all(assigneeIdChunks.map(fetchAllJobsOfAssignees))
      ).flat();

      // Assign jobs to drivers and track stacks
      const newStacks: Record<string, Job[]> = {};
      allFetchedJobs.forEach((job) => {
        const assigneeId = isTargetingVendor
          ? job.vendorJobAssignment?.vendorAccount?.id
          : job.driver?.id;

        if (assigneeId) {
          const startDate = dayjs.tz(job.jobStartAt);
          const columnIndex =
            DUMMY_COLUMN_INCREMENTS +
            Math.floor(
              startDate.hour() * INCREMENTS_PER_HOUR +
                startDate.minute() / MINUTES_PER_INCREMENT,
            );
          const slotKey = `${assigneeId}:${columnIndex}`;

          if (!newStacks[slotKey]) {
            newStacks[slotKey] = [];
          }
          newStacks[slotKey].push(job);

          newAssignments[slotKey] = job;

          assigneeJobCount[assigneeId] = (assigneeJobCount[assigneeId] ?? 0) + 1;
        }
      });

      // Determine if there are active jobs filters
      const hasActiveAssignedJobsFilters = Object.keys(assignedJobsFilters).some(
        (key) => assignedJobsFilters[key as keyof JobsFilters],
      );

      // Update job assignments and filter drivers
      const jobAssignments = {
        ...driverSchedulerStore.jobAssignments,
        ...newAssignments,
      };

      const jobStacks = {
        ...driverSchedulerStore.jobStacks,
        ...newStacks,
      };

      let filteredAssignees = driverSchedulerStore.assignees;
      if (hasActiveAssignedJobsFilters) {
        filteredAssignees = driverSchedulerStore.assignees.filter(
          (assignee) => assigneeJobCount[assignee.id],
        );
      }

      driverSchedulerStore.setJobAssignments(jobAssignments, jobStacks);
      driverSchedulerStore.setFilteredAssignees(filteredAssignees);
      driverSchedulerStore.fetchAssignedJobsEnd(allFetchedJobs);
      return allFetchedJobs;
    } catch (error) {
      if (requestSignal.aborted) {
        return [];
      }

      chunkRequestsController.abort();
      driverSchedulerStore.fetchAssignedJobsEnd([]);
      connection.handleRequestError(error, t('error_messages.jobs.failed_to_fetch'));
    }
  };

  const fetchDriversCancelToken = useCancelToken();
  const fetchDrivers = async (options: FetchAssigneesParams) => {
    driverSchedulerStore.assigneesFetchStart(options.loadMore);

    const { assigneesPagination, assigneesFilters, dateFilters } = driverSchedulerStore;
    const params: GetDriversQueryProps = {
      'page[limit]': assigneesPagination.limit,
    };
    if (assigneesPagination.before) {
      params['page[before]'] = assigneesPagination.before;
    } else if (assigneesPagination.after) {
      params['page[after]'] = assigneesPagination.after;
    }

    if (assigneesFilters.search) {
      params['search[query]'] = assigneesFilters.search;
    }

    if (dateFilters.startDate) {
      params['filter[date]'] = dayjs.tz(dateFilters.startDate).format('YYYY-MM-DD');
    }

    const cancelToken = fetchDriversCancelToken.issueToken();

    try {
      const response = await getV1CompaniesCompanyIdDriversTypeahead({
        path: { 'company-id': options.companyId },
        query: params,
        cancelToken,
      });
      const pagination = extractPagination(response);
      const parsedDrivers = response.data.data.map((driver) =>
        DriverBasic.parse(driver as Driver_Read),
      );

      driverSchedulerStore.assigneesFetchEnd(parsedDrivers, pagination, options.loadMore);

      return parsedDrivers;
    } catch (error) {
      if (axios.isCancel(error)) {
        return [];
      }

      driverSchedulerStore.assigneesFetchEnd([], undefined, options.loadMore);
      connection.handleRequestError(
        error,
        t('error_messages.drivers.failed_to_fetch_drivers'),
      );
    } finally {
      fetchDriversCancelToken.discardToken();
    }
  };

  const fetchVendors = async (options: FetchAssigneesParams) => {
    driverSchedulerStore.assigneesFetchStart(options.loadMore);

    const { assigneesPagination, assigneesFilters } = driverSchedulerStore;
    const params: GetVendorsQueryProps = {
      'page[limit]': assigneesPagination.limit,
      'filter[account_types]': [AccountType.VENDOR],
    };

    if (assigneesPagination.before) {
      params['page[before]'] = assigneesPagination.before;
    } else if (assigneesPagination.after) {
      params['page[after]'] = assigneesPagination.after;
    }

    if (assigneesFilters.search) {
      params['search[query]'] = assigneesFilters.search;
    }

    try {
      const response = await getV1CompaniesAccountsTypeahead({
        path: { 'company-id': options.companyId },
        query: params,
      });
      const pagination = extractPagination(response);
      const parsedVendors = response.data.data.map((account) =>
        AccountTypeahead.parse(account),
      );

      driverSchedulerStore.assigneesFetchEnd(parsedVendors, pagination, options.loadMore);

      return parsedVendors;
    } catch (error) {
      driverSchedulerStore.assigneesFetchEnd([], undefined, options.loadMore);
      connection.handleRequestError(
        error,
        t('error_messages.account.failed_to_fetch_company_accounts'),
      );
    }
  };

  const assignDriver = async (
    id: string,
    driverId: string,
    companyId: string,
    complianceOverride?: boolean,
    jobStartAt?: string,
  ): Promise<Job> => {
    driverSchedulerStore.updateJobStart();

    try {
      const response = await patchV1CompaniesCompanyIdJobsId({
        path: { 'company-id': companyId, id },
        body: {
          driver_id: driverId,
          ...(complianceOverride ? { compliance_override: true } : {}),
          ...(jobStartAt ? { job_start_at: jobStartAt } : {}),
        },
      });
      const formatted = Job.parse(response.data.data);
      driverSchedulerStore.updateJob(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_assign_driver'),
      );
      throw error;
    } finally {
      driverSchedulerStore.updateJobEnd();
    }
  };

  const assignVendor = async (
    id: string,
    vendorId: string,
    companyId: string,
    complianceOverride?: boolean,
  ): Promise<Job> => {
    driverSchedulerStore.updateJobStart();

    try {
      const response = await putV1CompaniesCompanyIdJobsJobIdAssignVendor({
        path: { 'company-id': companyId, 'job-id': id },
        body: {
          vendor_id: vendorId,
          ...(complianceOverride ? { compliance_override: true } : {}),
        },
      });
      const formatted = Job.parse(response.data.data);
      driverSchedulerStore.updateJob(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_assign_vendor'),
      );
      throw error;
    } finally {
      driverSchedulerStore.updateJobEnd();
    }
  };

  const unassignDriver = async (id: string, companyId: string): Promise<Job> => {
    driverSchedulerStore.updateJobStart();

    try {
      const response = await patchV1CompaniesCompanyIdJobsId({
        path: { 'company-id': companyId, id },
        body: {
          driver_id: null,
        },
      });
      const formatted = Job.parse(response.data.data);
      driverSchedulerStore.updateJob(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_unassign_driver'),
      );
      throw error;
    } finally {
      driverSchedulerStore.updateJobEnd();
    }
  };

  const unassignVendor = async (id: string, companyId: string): Promise<Job> => {
    driverSchedulerStore.updateJobStart();

    try {
      const response = await putV1CompaniesCompanyIdJobsJobIdUnassign({
        path: { 'company-id': companyId, 'job-id': id },
      });
      const formatted = Job.parse(response.data.data);
      driverSchedulerStore.updateJob(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_unassign_vendor'),
      );
      throw error;
    } finally {
      driverSchedulerStore.updateJobEnd();
    }
  };

  async function updateJob(
    id: string,
    data: UpdateJobFormData,
    companyId: string,
    inline?: false,
  ): Promise<Job>;
  async function updateJob(
    id: string,
    data: UpdateJobInlineData,
    companyId: string,
    inline: true,
  ): Promise<Job>;
  async function updateJob(
    id: string,
    data: UpdateJobData,
    companyId: string,
    inline?: boolean,
  ): Promise<Job> {
    driverSchedulerStore.updateJobStart();
    const deparsedModel = inline
      ? Job.deparseUpdateFromInlineMode(data as UpdateJobInlineData)
      : Job.deparseUpdate(data as UpdateJobFormData);

    try {
      const response = await patchV1CompaniesCompanyIdJobsId({
        path: { 'company-id': companyId, id },
        body: omit(deparsedModel, 'id'),
      });
      const formatted = Job.parse(response.data.data);
      driverSchedulerStore.updateJob(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.jobs.failed_to_update'));
      throw error;
    } finally {
      driverSchedulerStore.updateJobEnd();
    }
  }

  const subscribeToJobsRTU = (companyId: string) => {
    const cable = connection.realTimeConnection;
    const channel = realTimeChannels.CompanyJobUpdateChannel;

    let subscription = cable?.subscriptions.subscriptions.find((sub) => {
      return sub.identifier.includes(channel);
    });

    if (!subscription) {
      subscription = cable?.subscriptions?.create?.(
        { channel, company_id: companyId },
        {
          connected() {},
          disconnected() {},
          received: ({ data }: { data: Job_Read }) => {
            const job = Job.parse(data);
            driverSchedulerStore.updateJob(job);
          },
        },
      );
    }

    return subscription;
  };

  const getBulkSendDayJobsCount = useCallback(
    async ({ companyId, ...requestParams }: BulkSendDayJobsParams) => {
      const isTargetingVendor =
        requestParams.assigneesFilters.assigneeType === JobAssignmentType.VENDOR;
      const getBulkSendDayJobs = isTargetingVendor
        ? getV1CompaniesCompanyIdJobsBulkSendDayVendors
        : getV1CompaniesCompanyIdJobsBulkSendDayDrivers;
      const assigneeSearch = requestParams.assigneesFilters.search;

      try {
        const response = await getBulkSendDayJobs({
          path: { 'company-id': companyId },
          query: {
            ...buildJobsFacetQuery(requestParams.assignedJobsFilters),
            ...(assigneeSearch ? { 'search[query]': assigneeSearch } : {}),
            date: requestParams.date,
          },
        });

        return response.data.data.count;
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.jobs.failed_to_fetch_bulk_send_jobs_by_day_count'),
        );
        throw error;
      }
    },
    [],
  );

  const bulkSendDayJobs = useCallback(
    async ({ companyId, ...requestParams }: BulkSendDayJobsParams) => {
      const isTargetingVendor =
        requestParams.assigneesFilters.assigneeType === JobAssignmentType.VENDOR;
      const putBulkSendDayJobs = isTargetingVendor
        ? putV1CompaniesCompanyIdJobsBulkSendDayVendors
        : putV1CompaniesCompanyIdJobsBulkSendDayDrivers;

      try {
        const response = await putBulkSendDayJobs({
          path: { 'company-id': companyId },
          body: buildBulkSendDayJobsRequest(requestParams),
        });
        const { id: sendId, job_ids: jobIds } = response.data.data;
        enqueueBulkSend(sendId, jobIds);

        return jobIds;
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.jobs.failed_to_bulk_send_jobs_by_day'),
        );
        // Unlike the other bulk actions, rethrown so the Send All dialog stays open to retry.
        throw error;
      }
    },
    [enqueueBulkSend],
  );

  const fetchMoreAssignees = (companyId: string) => {
    const { assigneesFilters, hasMoreAssignees, isFetchingMoreAssignees } =
      driverSchedulerStore;

    if (!hasMoreAssignees || isFetchingMoreAssignees) {
      return;
    }

    if (assigneesFilters.assigneeType === 'vendor') {
      return fetchVendors({ companyId, loadMore: true });
    }

    return fetchDrivers({ companyId, loadMore: true });
  };

  return {
    assignDriver,
    assignVendor,
    bulkSendDayJobs,
    fetchAllUnassignedJobs,
    fetchAssignedJobs,
    fetchDrivers,
    fetchMoreAssignees,
    fetchUnassignedJobs,
    fetchVendors,
    getBulkSendDayJobsCount,
    subscribeToJobsRTU,
    unassignDriver,
    unassignVendor,
    updateJob,
  };
}
